// Natural-language relative date formatting for open-invite rounds.
// Mirrors the Postgres `format_relative_date()` function in
// 20260518162510_add_open_invite_rounds.sql - keep the rules in sync.
//
// Rules:
//   today                                → "today"
//   tomorrow                             → "tomorrow"
//   later this ISO week                  → "this <weekday>"
//   next ISO week                        → "next <weekday>"
//   same year, further out               → "on <Weekday> <D> <Month>"
//   different year                       → "on <Weekday> <D> <Month> <YYYY>"

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ISO week number - Monday is the first day of the week, week containing
// Jan 4 is week 1.
const isoWeek = (d) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // Shift so Sunday (0) becomes 7
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return {
    year: date.getUTCFullYear(),
    week: Math.ceil(((date - yearStart) / 86400000 + 1) / 7),
  };
};

// Days between two dates at midnight (local).
const daysBetween = (target, today) => {
  const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const b = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((a - b) / 86400000);
};

/**
 * Format a target date relative to today using natural language.
 *
 * @param {Date|string} target Date object or YYYY-MM-DD string
 * @param {Date}        [today=new Date()]
 * @returns {string}
 */
export const formatRelativeDate = (target, today = new Date()) => {
  const t = target instanceof Date ? target : new Date(`${target}T00:00:00`);
  if (Number.isNaN(t.getTime())) return '';

  const diff = daysBetween(t, today);
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';

  const weekday = WEEKDAYS[t.getDay()];
  const month = MONTHS[t.getMonth()];
  const dayNum = t.getDate();

  if (diff > 0) {
    const tISO = isoWeek(t);
    const today_iso = isoWeek(today);
    if (tISO.year === today_iso.year && tISO.week === today_iso.week) {
      return `this ${weekday}`;
    }
    if (
      (tISO.year === today_iso.year && tISO.week === today_iso.week + 1) ||
      (tISO.year === today_iso.year + 1 && today_iso.week >= 52 && tISO.week === 1)
    ) {
      return `next ${weekday}`;
    }
  }

  if (t.getFullYear() === today.getFullYear()) {
    return `on ${weekday} ${dayNum} ${month}`;
  }
  return `on ${weekday} ${dayNum} ${month} ${t.getFullYear()}`;
};

export default formatRelativeDate;
